/**
 * Trunnion AI - Chat Store
 * Manages agent chat state: messages, streaming, and active session.
 */

import { create } from 'zustand';
import type { ChatMessage, ChatToolCall, ChatReasoningEntry } from '@/types/chat.types';

interface ChatState {
  activeSessionId: string | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingContent: string;
  streamingToolCalls: ChatToolCall[];
  streamingReasoning: ChatReasoningEntry[];

  setActiveSession: (sessionId: string | null) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  setStreaming: (streaming: boolean) => void;
  appendStreamingContent: (token: string) => void;
  resetStreamingContent: () => void;
  addStreamingToolCall: (toolCall: ChatToolCall) => void;
  updateStreamingToolCall: (id: string, update: Partial<ChatToolCall>) => void;
  addStreamingReasoning: (entry: ChatReasoningEntry) => void;
  clearChat: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  activeSessionId: null,
  messages: [],
  isStreaming: false,
  streamingContent: '',
  streamingToolCalls: [],
  streamingReasoning: [],

  setActiveSession: (sessionId) =>
    set({ activeSessionId: sessionId, messages: [], streamingContent: '' }),

  setMessages: (messages) =>
    set({ messages }),

  addMessage: (message) =>
    set({ messages: [...get().messages, message] }),

  setStreaming: (streaming) =>
    set({ isStreaming: streaming }),

  appendStreamingContent: (token) =>
    set((state) => ({ streamingContent: state.streamingContent + token })),

  resetStreamingContent: () =>
    set({ streamingContent: '', streamingToolCalls: [], streamingReasoning: [] }),

  addStreamingToolCall: (toolCall) =>
    set((state) => ({ streamingToolCalls: [...state.streamingToolCalls, toolCall] })),

  updateStreamingToolCall: (id, update) =>
    set((state) => ({
      streamingToolCalls: state.streamingToolCalls.map((tc) =>
        tc.id === id ? { ...tc, ...update } : tc,
      ),
    })),

  addStreamingReasoning: (entry) =>
    set((state) => ({ streamingReasoning: [...state.streamingReasoning, entry] })),

  clearChat: () =>
    set({
      activeSessionId: null,
      messages: [],
      isStreaming: false,
      streamingContent: '',
      streamingToolCalls: [],
      streamingReasoning: [],
    }),
}));
